import { LitElement } from '~/lib/bn-lit-element';
import { html, css, renderIf } from '../../utility/lit-entity';
import '../../common/bn-property';
import './work-order-review-list-item';
import './probation-review-list-item';
import { LitSirenEntityMixin } from '../../utility/lit-siren-entity-mixin';
import { sharedStyles } from '../../styles/shared-styles';

class ReviewList extends LitSirenEntityMixin(LitElement) {
  constructor() {
    super();
    this.entities = [];
    this.fetchingCount = 0;
  }

  static get properties() {
    return {
      workOrdersHref: { type: String, attribute: 'work-orders-href' },
      probationsHref: { type: String, attribute: 'probations-href' },
      nextWorkOrdersHref: { type: String, attribute: false },
      nextProbationsHref: { type: String, attribute: false },
      entities: { type: Array, attribute: false },
      fetchingCount: { type: Number, attribute: false },
    };
  }

  static get styles() {
    return [sharedStyles, css`
      :host {
        display: flex;
        flex-direction: column;
        align-items: stretch;
      }
      button {
        align-self: center;
      }
      .message {
        text-align: center;
        margin: 1em;
      }
      .entities {
        display: flex;
        flex-direction: column;
      }
      .entities * {
        margin-bottom: 1em;
      }
      .entities *[hide] {
        margin-bottom: 0;
      }
    `];
  }

  updated(changedProperties) {
    if (!changedProperties.has('workOrdersHref') && !changedProperties.has('probationsHref')) {
      return;
    }
    if (!this.workOrdersHref || !this.probationsHref) {
      return;
    }

    this.entities = [];
    this.nextWorkOrdersHref = undefined;
    this.nextProbationsHref = undefined;
    this._loadAll(this.workOrdersHref, this.probationsHref);
  }

  render() {
    return html`
      ${this.renderEntities()}

      ${renderIf(this.nextWorkOrdersHref || this.nextProbationsHref, () => html`
        <button
          class="button button--outlined"
          @click="${() => this._loadAll(this.nextWorkOrdersHref, this.nextProbationsHref)}"
          ?disabled="${this.fetchingCount > 0}"
        >
          ${this.fetchingCount > 0 ? html`Loading <bn-icon name="fas fa-spinner fa-pulse"></bn-icon>` : 'More...'}
        </button>
      `)}
    `;
  }

  renderEntities() {
    if (this._isLoadingNewList()) {
      return html`<div class="message"><bn-loading></bn-loading></div>`;
    }
    if (!this.entities.length && this.fetchingCount === 0) {
      return html`<div class="message">No Reviews to display.</div>`;
    }
    return html`
      <div class="entities">
        ${this.entities.map((entity) => entity.class.includes('probation') ? this._renderProbationEntity(entity) : this._renderWorkOrderEntity(entity))}
      </div>
    `;
  }

  _renderWorkOrderEntity(entity) {
    return html`
      <work-order-review-list-item .href="${entity.getLink('self').href}"></work-order-review-list-item>
    `;
  }

  _renderProbationEntity(entity) {
    return html`
      <probation-review-list-item .href="${entity.getLink('self').href}"></probation-review-list-item>
    `;
  }

  async _load(href, subEntityClass) {
    if (!href) {
      return;
    }

    this.fetchingCount++;
    let nextLink;
    try {
      const entity = await this.fetchSiren(href);
      if (!entity) {
        return;
      }

      this.entities = [...this.entities, ...entity.getSubEntitiesByClass(subEntityClass)];
      this.entities.sort((a, b) => {
        return (a.properties.completedAt || a.properties.endDate) > (b.properties.completedAt || b.properties.endDate) ? -1 : 1;
      });
      nextLink = entity.getLinkByRel('next');
    } catch (e) {
      // eslint-disable-next-line no-console
      console.warn(`Unable to retrieve entity list at: ${href}`);
    }

    this.fetchingCount--;
    return nextLink ? nextLink.href : undefined;
  }

  async _loadAll(workOrdersHref, probationsHref) {
    [this.nextWorkOrdersHref, this.nextProbationsHref] = await Promise.all([
      this._load(workOrdersHref, 'work-order'),
      this._load(probationsHref, 'probation'),
    ]);
  }

  _isLoadingNewList() {
    return this.loading ||
      !this.workOrdersHref ||
      !this.probationsHref ||
      (!this.entities.length && this.fetchingCount > 0);
  }
}

window.customElements.define('review-list', ReviewList);
